<template>
  <div>
    <AM3InputSelect
      ref="selectRef"
      :model-value="multiple ? selectedOids : selectedList"
      :label="label"
      :placeholder="multiple ? 'Search or select lists' : 'Search or select a list'"
      :options="messageLists"
      :option-label="listOptionLabel"
      :option-value="multiple ? 'oid' : undefined"
      :multiple="multiple"
      :clearable="true"
      :validate="multiple ? undefined : validateRecipientCount"
      @update:model-value="handleModelValueUpdate"
    >
      <template #option="{ option }">
        <div class="flex items-center justify-between w-full gap-3 min-w-0">
          <span class="truncate">{{ listOptionLabel(option) }}</span>
          <AM3UserCountTag
            v-if="showRecipientCount"
            :count="recipientCountForList(option)"
          />
        </div>
      </template>
    </AM3InputSelect>

    <template v-if="!multiple && selectedList && showChannelToggles">
      <div class="">
        <div
          v-if="settings.listContainsEmail"
          class="flex justify-between items-center mt-4 group cursor-pointer"
        >
          <label
            class="flex items-center cursor-pointer w-full"
            @click="toggleEmail"
          >
            <span class="rounded-full w-8 h-8 bg-surfaces-neutral flex justify-center items-center">
              <AM3Icon type="email" />
            </span>
            <span class="ml-3 text-sm">Email</span>
          </label>
          <AM3Checkbox v-model="settings.emailEnabled" />
        </div>

        <div
          v-if="settings.listContainsSms"
          class="flex justify-between items-center mt-4 group cursor-pointer"
        >
          <label
            class="flex items-center cursor-pointer w-full"
            @click="toggleSms"
          >
            <span class="rounded-full w-8 h-8 bg-surfaces-neutral flex justify-center items-center">
              <AM3Icon type="sms" />
            </span>
            <span class="ml-3 text-sm">SMS</span>
          </label>
          <AM3Checkbox v-model="settings.smsEnabled" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import {
  getListOptionLabel,
  recipientCountForList as computeRecipientCountForList,
  validateRecipientCount as computeValidateRecipientCount,
} from './messageListSelectorLogic'
import { getContext } from '@/utils/general.ts'
import { useLiveEmailRecipientCount } from '@/composables/useLiveEmailRecipientCount'

const selectRef = ref(null)

const store = useStore()
const route = useRoute()

const props = defineProps({
  // Object shape (single-select mode) or an array of list oids (multiple mode).
  modelValue: {
    type: [Object, Array],
    default: () => ({
      oid: null,
      smsCanBeEnabled: false,
      emailCanBeEnabled: false,
      smsEnabled: false,
      emailEnabled: false,
      listContainsSms: false,
      listContainsEmail: false,
    }),
  },
  // When true, AM3InputSelect renders its built-in checkbox indicators and
  // accepts an array of list oids instead of a single list + channel toggles.
  // Visually this is the ONLY difference from single-select - the option
  // rows (name + recipient-count pill) render identically either way.
  multiple: {
    type: Boolean,
    default: false,
  },
  label: String,
  // Forwarded to $api.messageLists.fetchAll. Previously referenced but never
  // declared, so they always resolved to undefined.
  messageListType: {
    type: String,
    default: undefined,
  },
  channels: {
    type: Array,
    default: undefined,
  },
  // Whether to show the per-channel Email/SMS enable toggles after a list is
  // selected. Off for channel-specific contexts (e.g. the planner drawer, which
  // is already either SMS or Email) where they make no sense.
  showChannelToggles: {
    type: Boolean,
    default: true,
  },
  // When set, show each option's recipient count as a pill (never as text
  // appended to the label).
  showRecipientCount: {
    type: Boolean,
    default: false,
  },
  channel: {
    type: String, // 'email' | 'sms'
    default: undefined,
  },
  // Email only: the message's "Ignore suppressions" setting, so the live
  // recipient count matches what the send will include.
  ignoreSuppressed: {
    type: Boolean,
    default: false,
  },
  // Override the eventOid from route query. Used when the component is rendered
  // outside a routed page (e.g. in a drawer) where route.query is not set.
  eventOidOverride: {
    type: [Number, String],
    default: undefined,
  },
  // Override the eventGroupOid from route query. Like eventOidOverride, used
  // when rendered in a drawer. An event group has its own internal "all
  // attendees" list, fetched the same way as an event's.
  eventGroupOidOverride: {
    type: [Number, String],
    default: undefined,
  },
  // Override the campaignOid from route query. Like eventOidOverride, used when
  // rendered in a drawer (e.g. the campaign view) where route.query isn't set.
  // Resolves the campaign's signup list (_internal_campaign_<oid>_).
  campaignOidOverride: {
    type: [Number, String],
    default: undefined,
  },
})

const promoterOid = computed(() => store.state.authModule.account.promoterOid)
const selectedList = ref(null)
const selectedOids = ref(Array.isArray(props.modelValue) ? [...props.modelValue] : [])

const handleModelValueUpdate = (value) => {
  if (props.multiple) {
    selectedOids.value = value
    emit('update:modelValue', value)
  }
  else {
    selectedList.value = value
  }
}

const eventId = computed(() => props.eventOidOverride || route.query.eventOid || null)
const eventGroupId = computed(() => props.eventGroupOidOverride || route.query.eventGroupOid || null)
const campaignId = computed(() => props.campaignOidOverride || route.query.campaignOid || null)

// Email counts come from the live send-preview count for the selected list, so
// they match who the send reaches (EC-4647); everything else, and any list not
// yet counted, uses the list's stats snapshot (see messageListSelectorLogic.ts).
const liveEmailCount = useLiveEmailRecipientCount()
const recipientCountForList = list =>
  computeRecipientCountForList(list, props.channel, liveEmailCount.countFor(list?.oid))

// How to label each option (see messageListSelectorLogic.ts). Recipient
// counts are rendered separately as a pill (see #option slot), never
// appended to this label.
const listOptionLabel = computed(() => list => getListOptionLabel(list, {
  isEventContext: !!(eventId.value || eventGroupId.value),
  isCampaignContext: !!campaignId.value,
}))
const messageLists = ref([])
const settings = ref(props.multiple ? {} : { ...props.modelValue })
const isFetchingMessageLists = ref(false)

const fetchMessageLists = async () => {
  const { $api, $arNotification } = getContext()

  try {
    isFetchingMessageLists.value = true

    if (campaignId.value && props.messageListType !== 'manual') {
      messageLists.value = await $api.messageLists.fetchCampaignMessageLists(promoterOid.value, campaignId.value)
      if (messageLists.value[0] && !selectedList.value) {
        selectedList.value = messageLists.value[0]
      }
    }
    else if (eventId.value && props.messageListType !== 'manual') {
      messageLists.value = await $api.messageLists.fetchEventMessageLists(promoterOid.value, eventId.value)
      if (messageLists.value[0] && !selectedList.value) {
        selectedList.value = messageLists.value[0]
      }
    }
    else if (eventGroupId.value && props.messageListType !== 'manual') {
      messageLists.value = await $api.messageLists.fetchEventGroupMessageLists(promoterOid.value, eventGroupId.value)
      if (messageLists.value[0] && !selectedList.value) {
        selectedList.value = messageLists.value[0]
      }
    }
    else {
      messageLists.value = await $api.messageLists.fetchAll(promoterOid.value, {
        top: 'all',
        type: props.messageListType,
        channels: props.channels,
      })
    }

    // Hydrate the visible selection from a pre-set modelValue (edit mode), so an
    // already-saved list shows its name rather than the placeholder. Prefer the
    // persisted oid over any auto-selected first list.
    if (props.modelValue?.oid && !selectedList.value) {
      const match = messageLists.value.find(list => list.oid === props.modelValue.oid)
      if (match) selectedList.value = match
    }
  }
  catch (error) {
    console.error(error)
    $arNotification.push({
      type: 'error',
      message: 'Failed to fetch message lists',
    })
  }
  finally {
    isFetchingMessageLists.value = false
  }
}

watch(settings, (newVal) => {
  if (!props.multiple) emit('update:modelValue', newVal)
}, { deep: true })

watch(selectedList, (messageList) => {
  if (!messageList) {
    settings.value = {
      oid: null,
      smsCanBeEnabled: false,
      emailCanBeEnabled: false,
      smsEnabled: false,
      emailEnabled: false,
      listContainsSms: false,
      listContainsEmail: false,
      recipientCount: null,
    }
    return
  }

  settings.value = {
    oid: messageList.oid,
    emailCanBeEnabled: messageList.email,
    emailEnabled: messageList.email,
    listContainsEmail: messageList.email,
    smsCanBeEnabled: messageList.sms,
    smsEnabled: false,
    listContainsSms: messageList.sms,
    recipientCount: recipientCountForList(messageList),
  }
})

watch([() => selectedList.value?.oid, () => props.ignoreSuppressed], async ([messageListOid]) => {
  if (props.channel !== 'email' || props.multiple) return
  await liveEmailCount.refresh(promoterOid.value, messageListOid ?? null, props.ignoreSuppressed)
  if (selectedList.value && settings.value.oid === selectedList.value.oid) {
    settings.value.recipientCount = recipientCountForList(selectedList.value)
  }
})

const validateRecipientCount = list =>
  computeValidateRecipientCount(list, props.channel, liveEmailCount.countFor(list?.oid))

const emit = defineEmits(['update:modelValue', 'update:totalCount', 'update:selectedLists'])

// Recipient count kept in sync via v-model:total-count so parents (e.g. the
// "To" accordion title in messages/create.vue) can show a running total
// without re-deriving it from raw oids + stats themselves. Null (renders no
// badge) until at least one list is selected, matching AM3UserCountTag's own
// null-hides behaviour.
//
// Multi-select mode only counts the first selected list, not the sum of all
// of them: the backend (and messages/create.vue's meta.messageListOid) only
// ever sends to selectedOids[0], so summing every selection would show a
// bigger audience than the send will actually reach. Switch this back to
// summing every selected list once the server accepts multiple list oids.
const totalRecipientCount = computed(() => {
  if (props.multiple) {
    const first = messageLists.value.find(list => list.oid === selectedOids.value[0])
    return first ? recipientCountForList(first) : null
  }
  return selectedList.value ? recipientCountForList(selectedList.value) : null
})

watch(totalRecipientCount, count => emit('update:totalCount', count), { immediate: true })

// The selected list objects (with their stats snapshots), for parents that
// need more than the oids - e.g. SMS checking for US recipients before MMS.
watch([messageLists, selectedOids, selectedList], () => {
  emit('update:selectedLists', props.multiple
    ? selectedOids.value
        .map(oid => messageLists.value.find(list => list.oid === oid))
        .filter(Boolean)
    : (selectedList.value ? [selectedList.value] : []))
}, { deep: true })

// Let the parent trigger validation imperatively (e.g. when "Continue creating
// message" is clicked) so the "no valid recipients" error surfaces on demand.
const runValidation = () => selectRef.value?.runValidation()

defineExpose({ runValidation })

const toggleEmail = () => {
  settings.value.emailEnabled = !settings.value.emailEnabled
}

const toggleSms = () => {
  settings.value.smsEnabled = !settings.value.smsEnabled
}

fetchMessageLists()
</script>
